import { useMutation, useQueryClient } from '@tanstack/react-query';
import { paths } from '@/generated/supplier-experience-api';
import useTakerMarkets, { OfferConfig, TakerMarket } from '@/enterprise/data/useTakerMarkets';
import apiClient from '@/lib/apiClient';
import { usePendingOfferPollingStore } from '@/lib/offerStatusPolling/usePendingOfferPollingStore';
import { useRefetchStatsQueries } from '@/lib/serverSentEvents/useServerSideEventListeners';
import { useReporting } from '@/reporting';
import useDataDog from '@/reporting/DataDog';
import { ScheduleInput } from '../features/offerModal/types';
import {
  buildScheduleOptimisticUpdateData,
  buildSchedulePatchData,
  buildSchedulePostData,
} from '../features/offerModal/utils';
import { mapFrequencyToApiFormat } from './frequencyUtils';
import { determineOfferStatus, determineOptimisticIsEnabled } from './offerStatusUtils';

type RelevantOfferConfig = Pick<
  OfferConfig,
  'uuid' | 'isDiscountBidding' | 'maxDiscount' | 'maxApr' | 'exclusionSettings'
> & { expireOn?: string | null; schedule?: ScheduleInput | null; frequency?: 'ONE_TIME' | 'RECURRING' };

type Referrer = paths['/offers/price-discovery']['post']['requestBody']['content']['application/json']['referrer'];

export interface SubmitPriceDiscoveryOfferInput {
  marketUuid: string;
  offerConfig: RelevantOfferConfig;
  supplierDivisionUuid: string;
  referrer?: Referrer;
}

const useSubmitPriceDiscoveryOffer = () => {
  const queryClient = useQueryClient();
  const { data: takerMarkets } = useTakerMarkets();
  const { refetchOneTakerMarket } = useRefetchStatsQueries();
  const { addError } = useDataDog();
  const { trackEnterpriseEvent } = useReporting();
  const { addPendingMarkets } = usePendingOfferPollingStore();

  return useMutation({
    mutationKey: ['submitPriceDiscoveryOffer'],
    mutationFn: async ({ marketUuid, offerConfig, supplierDivisionUuid, referrer }: SubmitPriceDiscoveryOfferInput) => {
      const takerMarket = takerMarkets?.find(
        (meta) => meta.marketUuid === marketUuid && meta.takerDivisionUuid === supplierDivisionUuid
      );

      const rate = offerConfig.isDiscountBidding ? offerConfig.maxDiscount : offerConfig.maxApr;
      const schedule = offerConfig.schedule;

      const response =
        offerConfig && offerConfig.uuid
          ? await apiClient.patch('api/supplier-experience/offers/price-discovery', {
              json: {
                groupingKey: 'SUPPLIER_MARKET',
                status: determineOfferStatus(schedule),
                offers: [
                  {
                    makerOrganizationUuid: takerMarket?.makerOrganizationUuid,
                    uuid: offerConfig.uuid,
                  },
                ],
                rateInformation: {
                  rateType: offerConfig.isDiscountBidding ? 'DISC' : 'APR',
                  rate: rate ?? 0,
                },
                expirationDate: offerConfig.expireOn,
                schedule: buildSchedulePatchData(offerConfig.schedule, takerMarket),
                frequency: offerConfig.frequency ? mapFrequencyToApiFormat(offerConfig.frequency) : undefined,
                referrer,
              } satisfies paths['/offers/price-discovery']['patch']['requestBody']['content']['application/json'],
            })
          : await apiClient.post('api/supplier-experience/offers/price-discovery', {
              json: {
                groupingKey: 'SUPPLIER_MARKET',
                frequency: mapFrequencyToApiFormat(offerConfig.frequency),
                offerType: 'price-discovery',
                rateType: offerConfig.isDiscountBidding ? 'DISC' : 'APR',
                rate: rate ?? 0,
                expirationDate: offerConfig.expireOn ?? undefined, // Allow null to coalesce to undefined for POST because this is a net new offer.
                schedule: buildSchedulePostData(offerConfig.schedule),
                supplierMarkets: [
                  {
                    makerOrganizationUuid: takerMarket?.makerOrganizationUuid,
                    marketUuid,
                    supplierDivisionUuid,
                  },
                ],
                referrer,
              } satisfies paths['/offers/price-discovery']['post']['requestBody']['content']['application/json'],
            });

      if (!offerConfig.uuid) {
        if (response.status === 201) {
          const body =
            await response.json<
              paths['/offers/price-discovery']['post']['responses']['201']['content']['application/json']
            >();
          return { status: response.status, offerUuid: body.data[0]?.uuid };
        }

        if (response.status === 202) {
          const body =
            await response.json<
              paths['/offers/price-discovery']['post']['responses']['202']['content']['application/json']
            >();
          return { status: response.status, offerUuid: body.data.offers[0]?.uuid };
        }
      }

      return { status: response.status };
    },
    onSuccess: (result, variables) => {
      const tmMeta = takerMarkets?.find(
        (meta) => meta.marketUuid === variables.marketUuid && meta.takerDivisionUuid === variables.supplierDivisionUuid
      );

      if (tmMeta?.marketUuid && tmMeta?.takerDivisionId) {
        // If placing first offer, use returned offer uuid from API response to update cache
        // User can interact with offer before stats and offer refetches
        const offerUuid = tmMeta?.offerConfig.uuid ?? result.offerUuid;

        const status = determineOfferStatus(variables.offerConfig.schedule);

        // update the cache with the returned data
        queryClient.setQueryData<TakerMarket[]>(['taker-markets'], (prevTakerMarkets) =>
          (prevTakerMarkets ?? []).map((tm) => ({
            ...(tm.marketUuid === variables.marketUuid && tm.takerDivisionUuid === variables.supplierDivisionUuid
              ? {
                  ...tm,
                  offerConfig: {
                    ...tm.offerConfig,
                    ...variables.offerConfig,
                    isEnabled: determineOptimisticIsEnabled(status, tmMeta.offerConfig.isEnabled ?? false),
                    uuid: offerUuid,
                  },
                  disableAfterMarketClearsDate: variables.offerConfig.expireOn ?? null,
                  offerSchedule: buildScheduleOptimisticUpdateData(variables.offerConfig.schedule, tm),
                }
              : tm),
          }))
        );

        if (result.status === 202) {
          addPendingMarkets([
            {
              marketUuid: tmMeta.marketUuid,
              takerDivisionUuid: tmMeta.takerDivisionUuid,
              makerOrganizationUuid: tmMeta.makerOrganizationUuid,
            },
          ]);
        } else {
          // offer resolved synchronously, refetch immediately
          trackEnterpriseEvent('polling-update::in-time', {
            marketUuid: tmMeta.marketUuid,
            takerDivisionUuid: tmMeta.takerDivisionUuid,
            makerOrganizationUuid: tmMeta.makerOrganizationUuid,
          });
          refetchOneTakerMarket({ marketUuid: tmMeta.marketUuid, takerId: tmMeta.takerDivisionId });
        }
      }
    },
    onError: (error, variables) => {
      addError('useSubmitPriceDiscoveryOffer', {
        error,
        variables,
        seaOfferUuid: variables.offerConfig.uuid,
      });
    },
  });
};

export default useSubmitPriceDiscoveryOffer;
